import {
    RuleSupport,
    TimeZoneSupportStatus,
    version
} from "@clhbid/canadian-time-zone-hotpatch";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import ReactGA from "react-ga4";
import { inspectHostSupport } from "./timeZoneHotpatch";

// Shared with clhbid.com so both sites' stale-host share can be read together.
export const TIME_ZONE_SUPPORT_EVENT = "timezone_support_checked";
export const TIME_ZONE_SUPPORT_POSTHOG_EVENT = "TimeZoneSupport.checked";
const SITE = "auctions";

// Set before probing and never reset, so each rule is counted at most once
// per page load: a double count distorts the denominator, a lost event only
// shrinks it.
let hasTracked = false;

// Each destination is best effort and fails on its own; nothing here may
// reach the app.
const attempt = (vendor: string, send: () => void) => {
    try {
        send();
    } catch (error) {
        try {
            Sentry.captureException(error, {
                tags: { feature: "telemetry", vendor }
            });
        } catch {
            // Nowhere left to report the failure.
        }
    }
};

const probeRuleSupport = (): readonly RuleSupport[] => {
    let ruleSupport: readonly RuleSupport[] = [];
    attempt("canadian-time-zone-hotpatch", () => {
        ruleSupport = inspectHostSupport().ruleSupport;
    });
    return ruleSupport;
};

// A `rule_outdated` host is ahead of the package, so the package's rule table,
// not the visitor, needs updating.
const warnRuleOutdated = ({ ruleId }: RuleSupport) =>
    attempt("sentry", () =>
        Sentry.captureMessage(
            `Time zone rule ${ruleId} is outdated on this host`,
            {
                level: "warning",
                fingerprint: ["timezone-rule-outdated", ruleId],
                tags: { rule_id: ruleId, package_version: version }
            }
        )
    );

// Reports whether this visitor's host knows each Canadian permanent-time rule.
// Call it once GA is initialized; later calls send nothing.
export const trackTimeZoneSupport = () => {
    if (hasTracked) return;
    hasTracked = true;

    for (const rule of probeRuleSupport()) {
        const properties = {
            site: SITE,
            rule_id: rule.ruleId,
            rule_status: rule.status,
            package_version: version
        };

        attempt("google-analytics", () =>
            ReactGA.event(TIME_ZONE_SUPPORT_EVENT, properties)
        );
        attempt("posthog", () =>
            posthog.capture(TIME_ZONE_SUPPORT_POSTHOG_EVENT, properties)
        );
        if (rule.status === TimeZoneSupportStatus.rule_outdated) {
            warnRuleOutdated(rule);
        }
    }
};
